import { useState } from 'react';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { FilePicker } from '@/components/ui/FilePicker';
import { Input } from '@/components/ui/Input';
import { Select, Textarea } from '@/components/ui/Select';
import { apiUrl } from '@/lib/env';

import { uploadWebsiteImage } from './websiteApi';
import styles from './WebsiteContent.module.css';

export function WebsiteText({
  label,
  value,
  onChange,
  type = 'text',
  hint,
  required = false,
  disabled = false,
  multiline = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date' | 'datetime-local' | 'number' | 'url';
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  multiline?: boolean;
}) {
  return (
    <Field label={label} required={required} hint={hint}>
      {(ids) =>
        multiline ? (
          <Textarea
            id={ids.id}
            aria-describedby={ids.describedBy}
            rows={4}
            value={value}
            required={required}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        ) : (
          <Input
            id={ids.id}
            aria-describedby={ids.describedBy}
            type={type}
            value={value}
            required={required}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        )
      }
    </Field>
  );
}

export function WebsiteSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | '';
  options: readonly { value: T; label: string }[];
  onChange: (value: T | '') => void;
}) {
  return (
    <Field label={label}>
      {(ids) => (
        <Select
          {...ids}
          value={value}
          placeholder="Choose an option"
          options={[...options]}
          onChange={(event) => {
            onChange(
              options.find((option) => option.value === event.target.value)?.value ?? '',
            );
          }}
        />
      )}
    </Field>
  );
}

export function WebsiteImageField({
  entryId,
  label,
  imageId,
  alt,
  onChange,
  onBusy,
}: {
  entryId: string;
  label: string;
  imageId: string | null;
  alt: string;
  onChange: (id: string | null, alt: string) => void;
  onBusy: (change: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  async function upload(file: File) {
    setBusy(true);
    setError('');
    onBusy(1);
    try {
      const result = await uploadWebsiteImage(entryId, file);
      setFileName(file.name);
      onChange(result.id, alt);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'The image did not upload.');
    } finally {
      onBusy(-1);
      setBusy(false);
    }
  }

  return (
    <div className={styles.imageField}>
      <Field
        label={label}
        hint="PNG, JPEG or WebP up to 5 MB."
        error={error || undefined}
        className={styles.imagePicker ?? ''}
      >
        {({ id, describedBy, invalid }) => (
          <FilePicker
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            accept="image/png,image/jpeg,image/webp"
            busy={busy}
            fileName={imageId ? fileName || 'Image uploaded' : ''}
            onPick={(file) => void upload(file)}
          />
        )}
      </Field>
      {imageId ? (
        <div className={styles.imagePreview}>
          <img src={apiUrl(`/api/v1/website/images/${imageId}`)} alt={alt} />
          <Button
            variant="ghost"
            size="sm"
            icon={<X size={14} />}
            onClick={() => {
              setFileName('');
              setError('');
              onChange(null, '');
            }}
            disabled={busy}
          >
            Remove image
          </Button>
        </div>
      ) : null}
      {imageId ? (
        <WebsiteText
          label="Image description"
          value={alt}
          onChange={(value) => {
            onChange(imageId, value);
          }}
          required
        />
      ) : null}
    </div>
  );
}
